import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface ButtonProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
}
export const Button = forwardRef<HTMLAnchorElement, ButtonProps>(({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth,
  children,
  ...props
}, ref) => {
  const isOutline = variant === 'outline';
  return <motion.a ref={ref} whileHover={{
    scale: isOutline ? 1.02 : 1.04
  }} whileTap={{
    scale: 0.96
  }} animate={isOutline ? undefined : {
    y: [0, -2, 0]
  }} transition={{
    scale: {
      type: 'spring',
      stiffness: 400,
      damping: 17
    },
    y: {
      duration: 2.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }} className={cn('group relative overflow-hidden inline-flex items-center justify-center rounded-full font-heading font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 will-change-transform', {
    'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 focus:ring-emerald-500': variant === 'primary',
    'bg-celeste-500 text-white hover:bg-celeste-600 shadow-lg shadow-celeste-500/30 hover:shadow-xl hover:shadow-celeste-500/40 focus:ring-celeste-500': variant === 'secondary',
    'border-2 border-stone-200 bg-transparent text-stone-700 hover:border-stone-300 hover:bg-stone-50 focus:ring-stone-500': variant === 'outline',
    'px-4 py-2 text-sm': size === 'sm',
    'px-6 py-3 text-base': size === 'md',
    'px-8 py-4 text-lg': size === 'lg',
    'px-10 py-5 text-xl': size === 'xl',
    'w-full': fullWidth
  }, className)} {...props} data-id="element-378">
        {/* Periodic light sweep to draw the eye (skipped for subtle outline buttons) */}
        {!isOutline && <motion.span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-20deg] bg-white/30 blur-md" initial={{
      x: '-150%'
    }} animate={{
      x: ['-150%', '450%']
    }} transition={{
      duration: 1.1,
      ease: 'easeInOut',
      repeat: Infinity,
      repeatDelay: 2.6
    }} data-id="element-379" />}
        <span className="relative z-10 inline-flex items-center justify-center gap-2 transition-transform duration-200 group-hover:scale-[1.01]" data-id="element-380">
          {children}
        </span>
      </motion.a>;
});
Button.displayName = 'Button';